import React, { useState, useEffect, useMemo, useCallback } from 'react';
import Sidebar from './Sidebar';
import AdmissionReport from './AdmissionReport';
import FinanceReport from './FinanceReport';
import BedAllocationReport from './BedAllocationReport';

// ── Helpers ────────────────────────────────────────────────────────────────────
const fmt = (n) => Number(n || 0).toLocaleString('en-IN');
const fmtDate = (d) => {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};
const fmtDateTime = (d) => {
  if (!d) return '—';
  return new Date(d).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

// ── Mini SVG Line Chart ─────────────────────────────────────────────────────────
const LineChart = ({ data, keys, colors }) => {
  const W = 700, H = 200, PAD = 40;
  if (!data || data.length === 0) return <div style={{ height: H, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#C4BEB8', fontSize: 14 }}>No data for selected period</div>;

  const maxVal = Math.max(1, ...data.flatMap(d => keys.map(k => d[k] || 0)));
  const xStep = (W - PAD * 2) / Math.max(1, data.length - 1);

  const xOf = (i) => PAD + i * xStep;
  const yOf = (v) => H - PAD - ((v / maxVal) * (H - PAD * 2));

  const path = (key) => data.map((d, i) => `${i === 0 ? 'M' : 'L'}${xOf(i)},${yOf(d[key] || 0)}`).join(' ');

  return (
    <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height: H, overflow: 'visible' }}>
      {/* Grid lines */}
      {[0, 0.25, 0.5, 0.75, 1].map((t, i) => (
        <line key={i} x1={PAD} y1={H - PAD - t * (H - PAD * 2)} x2={W - PAD} y2={H - PAD - t * (H - PAD * 2)}
          stroke="#E8E4DF" strokeWidth="1" strokeDasharray={i === 0 ? 'none' : '4,4'} />
      ))}
      {/* X labels */}
      {data.map((d, i) => (
        <text key={i} x={xOf(i)} y={H - 8} textAnchor="middle" fontSize="10" fill="#9B9B9B" fontFamily="Work Sans, sans-serif">{d.label}</text>
      ))}
      {/* Lines + dots */}
      {keys.map((key) => (
        <g key={key}>
          <path d={path(key)} fill="none" stroke={colors[key]} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
          {data.map((d, i) => (
            <circle key={i} cx={xOf(i)} cy={yOf(d[key] || 0)} r="4" fill={colors[key]} stroke="white" strokeWidth="2" />
          ))}
        </g>
      ))}
    </svg>
  );
};

// ── Status + Source pills ──────────────────────────────────────────────────────
const StatusPill = ({ status }) => {
  const map = {
    PENDING:  { bg: 'rgba(245,158,11,0.12)', color: '#F59E0B', label: 'Pending' },
    APPROVED: { bg: 'rgba(45,95,79,0.12)',   color: '#2D5F4F', label: 'Confirmed' },
    REJECTED: { bg: 'rgba(232,93,74,0.12)',  color: '#E85D4A', label: 'Rejected' },
    DRAFT:    { bg: 'rgba(91,141,184,0.12)', color: '#3a6d97', label: 'Draft' },
  };
  const s = map[status?.toUpperCase()] || map.PENDING;
  return <span style={{ display:'inline-flex', alignItems:'center', padding:'4px 10px', borderRadius:20, fontSize:10.5, fontWeight:700, letterSpacing:0.8, textTransform:'uppercase', background:s.bg, color:s.color }}>{s.label}</span>;
};

const PAGE_SIZE = 10;

// ── Main Component ─────────────────────────────────────────────────────────────
const ReportsScreen = ({ onNavigate, onLogout, reportType }) => {
  // ✅ Auto-open report from sidebar submenu click
  const [view, setView]           = useState(() => {
    if (reportType === 'admission') return 'admission';
    if (reportType === 'finance') return 'finance';
    if (reportType === 'bed-allocation') return 'bed-allocation';
    if (reportType && !['registration','admission','finance','bed-allocation'].includes(reportType)) return 'coming-soon';
    return 'registration';
  });

  // ✅ React when reportType prop changes (user clicks different submenu)
  useEffect(() => {
    if (reportType === 'admission')   { setView('admission'); }
    else if (reportType === 'finance') { setView('finance'); }
    else if (reportType === 'bed-allocation') { setView('bed-allocation'); }
    else if (reportType && !['registration','admission','finance','bed-allocation'].includes(reportType)) { setCsType(reportType); setView('coming-soon'); }
    else { setView('registration'); }
  }, [reportType]);
  const [csType, setCsType]       = useState(reportType && reportType !== 'registration' ? reportType : '');
  const [students, setStudents]   = useState([]);
  const [loading, setLoading]     = useState(false);
  const [period, setPeriod]       = useState('week');
  const [dateOffset, setOffset]   = useState(0);
  const [filterStatus, setFStatus]= useState('all');
  const [filterCity, setFCity]    = useState('all');
  const [filterYear, setFYear]    = useState('all');
  const [search, setSearch]       = useState('');
  const [sort, setSort]           = useState({ col: 'date', dir: 'desc' });
  const [page, setPage]           = useState(1);
  // ✅ Real data from backend
  const [stats, setStats]         = useState({ total:0, confirmed:0, pending:0, rejected:0, convRate:0 });
  const [chartData, setChartData] = useState([]);
  const [cities, setCities]       = useState([]);
  const [totalFiltered, setTotalFiltered] = useState(0);
  const [totalPages, setTotalPages]       = useState(1);

  // Load report data from backend
  useEffect(() => {
    if (view !== 'registration') return;
    setLoading(true);

    // ✅ Calculate fromDate/toDate — no timezone issues
    const fmt = (d) => `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
    const now = new Date();
    const today = fmt(now);
    let fromDate, toDate;

    if (period === 'day') {
      const d = new Date(now.getFullYear(), now.getMonth(), now.getDate() + dateOffset);
      fromDate = toDate = fmt(d);
      // cap future at today
      if (fromDate > today) { fromDate = toDate = today; }
    } else if (period === 'week') {
      const mon = new Date(now.getFullYear(), now.getMonth(), now.getDate() - ((now.getDay()||7)-1) + dateOffset*7);
      const sun = new Date(mon.getFullYear(), mon.getMonth(), mon.getDate() + 6);
      fromDate = fmt(mon);
      toDate   = fmt(sun) > today ? today : fmt(sun);
    } else {
      // month
      const m       = new Date(now.getFullYear(), now.getMonth() + dateOffset, 1);
      const lastDay = new Date(m.getFullYear(), m.getMonth() + 1, 0);
      fromDate = fmt(m);
      toDate   = fmt(lastDay) > today ? today : fmt(lastDay);
    }

    const params = new URLSearchParams({
      status:   filterStatus !== 'all' ? filterStatus : '',
      city:     filterCity   !== 'all' ? filterCity   : '',
      year:     filterYear   !== 'all' ? filterYear   : '',
      search:   search || '',
      sortBy:   sort.col,
      sortDir:  sort.dir,
      page:     page - 1,
      size:     PAGE_SIZE,
      fromDate: fromDate,   // ✅ send date range
      toDate:   toDate,     // ✅ send date range
      period:   period,     // ✅ send period for trend chart
    });
    import('../services/api').then(({ default: api }) => {
      api.get(`/reports/registration?${params}`)
        .then(res => {
          const d = res.data;
          setStudents(d.records || []);
          setStats({
            total:     d.total,
            confirmed: d.confirmed,
            pending:   d.pending,
            rejected:  d.rejected,
            convRate:  d.conversionRate,
          });
          setChartData(d.trend || []);
          setCities(d.cities || []);
          setTotalFiltered(d.totalRecords || 0);
          setTotalPages(d.totalPages || 1);
        })
        .catch(() => {})
        .finally(() => setLoading(false));
    });
  }, [view, filterStatus, filterCity, filterYear, search, sort, page, period, dateOffset]);

  const openReport = (type) => {
    if (type === 'registration') { setView('registration'); }
    else if (type === 'admission') { setView('admission'); }
    else { setCsType(type); setView('coming-soon'); }
  };

  // ── Period label ────────────────────────────────────────────────────────────
  const periodLabel = useMemo(() => {
    const now = new Date();
    if (period === 'day') {
      const d = new Date(now); d.setDate(d.getDate() + dateOffset);
      return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' });
    }
    if (period === 'week') {
      const mon = new Date(now); mon.setDate(mon.getDate() - mon.getDay() + 1 + dateOffset * 7);
      const sun = new Date(mon); sun.setDate(sun.getDate() + 6);
      return `${mon.toLocaleDateString('en-IN', { day:'numeric', month:'short' })} – ${sun.toLocaleDateString('en-IN', { day:'numeric', month:'short', year:'numeric' })}`;
    }
    const m = new Date(now.getFullYear(), now.getMonth() + dateOffset, 1);
    return m.toLocaleDateString('en-IN', { month: 'long', year: 'numeric' });
  }, [period, dateOffset]);

  // ✅ Chart data comes from backend (setChartData in useEffect)

  // ✅ Filtering + sorting + pagination handled by backend
  const filtered = students; // already filtered by backend
  const pageData = students; // already paginated by backend

  const sortBy = (col) => {
    setSort(s => ({ col, dir: s.col === col && s.dir === 'asc' ? 'desc' : 'asc' }));
    setPage(1);
  };

  // Stats come from backend via setStats in useEffect

  // Cities come from backend (setCities in useEffect)

  // ── Export XLSX ─────────────────────────────────────────────────────────────
  const exportXLSX = useCallback(() => {
    import('xlsx').then(XLSX => {
      const headers = ['Reg ID','Full Name','Date','City/Region','Mobile','Status'];
      const rows = students.map(s => [
        s.registrationId||'',
        s.fullName||'',
        fmtDateTime(s.createdAt),
        s.nativeRegion||'',
        s.mobile||'',
        s.status||'',
      ]);
      const wb = XLSX.utils.book_new();
      const summary = [
        ['Omkara Hostel — Student Registration Report'],
        ['Generated', new Date().toLocaleDateString('en-IN', { day:'numeric', month:'long', year:'numeric' })],
        [],
        ['SUMMARY'],
        ['Total',     stats.total],
        ['Confirmed', stats.confirmed],
        ['Pending',   stats.pending],
        ['Rejected',  stats.rejected],
        ['Conversion Rate', stats.convRate + '%'],
      ];
      XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(summary), 'Summary');
      XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet([headers, ...rows]), 'Records');
      XLSX.writeFile(wb, 'Omkara_Registration_Report.xlsx');
    });
  }, [filtered, stats]);

  // ── Styles ──────────────────────────────────────────────────────────────────
  const S = {
    wrap:       { padding: '36px 40px', flex: 1 },
    card:       { background:'#fff', borderRadius:12, padding:28, boxShadow:'0 2px 12px rgba(0,0,0,0.06)', transition:'all 0.28s ease', cursor:'pointer', border:'2px solid transparent', position:'relative', overflow:'hidden' },
    cardActive: { borderColor:'#D4AF37', boxShadow:'0 8px 30px rgba(212,175,55,0.2)' },
    btn:        (variant='primary') => ({
      padding:'9px 20px', border:'none', borderRadius:6,
      fontFamily:'Work Sans, sans-serif', fontWeight:600, fontSize:12,
      textTransform:'uppercase', letterSpacing:1, cursor:'pointer',
      display:'flex', alignItems:'center', gap:7, transition:'all 0.2s',
      background: variant === 'gold' ? '#D4AF37' : variant === 'outline' ? 'transparent' : '#1B3B5F',
      color: variant === 'gold' ? '#1B3B5F' : variant === 'outline' ? '#1B3B5F' : '#fff',
      border: variant === 'outline' ? '1.5px solid #E8E4DF' : 'none',
    }),
    select:     { padding:'9px 14px', borderRadius:7, border:'1.5px solid #E8E4DF', fontFamily:'Work Sans, sans-serif', fontSize:12.5, color:'#2C2C2C', background:'#fff', cursor:'pointer', outline:'none' },
    tab:        (active) => ({ padding:'8px 18px', borderRadius:6, fontSize:12, fontWeight:600, letterSpacing:0.8, textTransform:'uppercase', cursor:'pointer', fontFamily:'Work Sans, sans-serif', border:'none', background: active ? '#1B3B5F' : 'transparent', color: active ? '#fff' : '#6B6B6B', transition:'all 0.2s' }),
    th:         { padding:'12px 18px', textAlign:'left', fontSize:10, letterSpacing:1.5, textTransform:'uppercase', color:'#6B6B6B', fontWeight:700, borderBottom:'1px solid #E8E4DF', whiteSpace:'nowrap', cursor:'pointer', background:'#F8F6F3' },
    td:         { padding:'13px 18px', fontSize:13.5, borderBottom:'1px solid #E8E4DF', color:'#2C2C2C', verticalAlign:'middle' },
  };

  // ── Render ──────────────────────────────────────────────────────────────────
  return (
    <div style={{ display:'flex', minHeight:'100vh', background:'#F8F6F3' }}>
      <Sidebar activeItem="reports" onNavigate={onNavigate} onLogout={onLogout} />

      <main style={{ flex:1, marginLeft:280, display:'flex', flexDirection:'column' }}>
        {/* Top bar */}
        <div style={{ background:'#fff', padding:'22px 40px', borderBottom:'1px solid #E8E4DF', position:'sticky', top:0, zIndex:50 }}>
          <div style={{ fontSize:13, color:'#6B6B6B', marginBottom:4 }}>
            <span>Reports</span>
            <span style={{ color:'#D4AF37', fontWeight:600 }}> › </span>
            <span style={{ color:'#D4AF37', fontWeight:600 }}>{ view === 'registration' ? 'Student Registration' : view === 'admission' ? 'Admissions' : view === 'finance' ? 'Finance' : view === 'bed-allocation' ? 'Bed Allocation' : csType }</span>
          </div>
          <div style={{ fontFamily:'Cormorant Garamond, serif', fontSize:30, fontWeight:400, color:'#1B3B5F' }}>
            {view === 'registration' ? 'Student Registration Report' : view === 'admission' ? 'Admissions Report' : view === 'finance' ? 'Finance Reports' : view === 'bed-allocation' ? 'Bed Allocation Report' : 'Report Coming Soon'}
          </div>
        </div>

        <div style={S.wrap}>

          {/* Overview removed — registration report shown directly */}

          {/* ── Finance Report ─────────────────────────────────────────────── */}
          {view === 'finance' && <FinanceReport />}
          {view === 'bed-allocation' && <BedAllocationReport />}

          {/* ── Admission Report ─────────────────────────────────────────── */}
          {view === 'admission' && <AdmissionReport />}

          {/* ── Coming Soon ─────────────────────────────────────────────────── */}
          {view === 'coming-soon' && (
            <div style={{ textAlign:'center', padding:'80px 0' }}>
              <div style={{ fontSize:60, marginBottom:16, opacity:0.3 }}>📊</div>
              <div style={{ fontFamily:'Cormorant Garamond, serif', fontSize:26, color:'#6B6B6B', marginBottom:8 }}>Report Coming Soon</div>
              <div style={{ fontSize:14, color:'#C4BEB8', marginBottom:32 }}>This report module is currently under development and will be available shortly.</div>
              <button style={S.btn()} onClick={() => { setView('registration'); onNavigate('reports', { reportType: 'registration' }); }}>← Back to Registration Report</button>
            </div>
          )}

          {/* ── Registration Report ─────────────────────────────────────────── */}
          {view === 'registration' && (
            <div>
              {/* Controls bar */}
              <div style={{ background:'#fff', borderRadius:12, padding:'24px 30px', boxShadow:'0 2px 12px rgba(0,0,0,0.06)', marginBottom:24, display:'flex', alignItems:'center', justifyContent:'space-between', flexWrap:'wrap', gap:16 }}>
                <div>
                  <h2 style={{ fontFamily:'Cormorant Garamond, serif', fontSize:26, fontWeight:500, color:'#1B3B5F', marginBottom:4 }}>Student Registration Report</h2>
                  <p style={{ fontSize:13, color:'#6B6B6B' }}>Showing data for {periodLabel}</p>
                </div>
                <div style={{ display:'flex', gap:12, alignItems:'center', flexWrap:'wrap' }}>
                  {/* Period tabs */}
                  <div style={{ display:'flex', background:'#F8F6F3', borderRadius:8, padding:4, gap:2 }}>
                    {['day','week','month'].map(p => (
                      <button key={p} style={S.tab(period === p)} onClick={() => { setPeriod(p); setOffset(0); }}>
                        {p.charAt(0).toUpperCase() + p.slice(1)}
                      </button>
                    ))}
                  </div>
                  {/* Date nav */}
                  <div style={{ display:'flex', alignItems:'center', gap:10, background:'#fff', border:'1.5px solid #E8E4DF', borderRadius:8, padding:'6px 14px' }}>
                    <button onClick={() => setOffset(o => o - 1)} style={{ width:28, height:28, borderRadius:6, border:'none', background:'#F8F6F3', cursor:'pointer', fontSize:14, color:'#1B3B5F' }}>‹</button>
                    <span style={{ fontSize:13, fontWeight:600, color:'#1B3B5F', minWidth:160, textAlign:'center' }}>{periodLabel}</span>
                    <button onClick={() => setOffset(o => o + 1)} style={{ width:28, height:28, borderRadius:6, border:'none', background:'#F8F6F3', cursor:'pointer', fontSize:14, color:'#1B3B5F' }}>›</button>
                  </div>
                </div>
              </div>

              {/* Summary cards */}
              <div style={{ display:'grid', gridTemplateColumns:'repeat(5,1fr)', gap:16, marginBottom:24 }}>
                {[
                  { label:'Total',         value: stats.total,     color:'#1B3B5F',  border:'#1B3B5F' },
                  { label:'Confirmed',     value: stats.confirmed, color:'#2D5F4F',  border:'#2D5F4F' },
                  { label:'Pending',       value: stats.pending,   color:'#F59E0B',  border:'#F59E0B' },
                  { label:'Rejected',      value: stats.rejected,  color:'#E85D4A',  border:'#E85D4A' },
                  { label:'Conv. Rate',    value: `${stats.convRate}%`, color:'#5B8DB8', border:'#5B8DB8' },
                ].map(c => (
                  <div key={c.label} style={{ background:'#fff', borderRadius:10, padding:'18px 20px', boxShadow:'0 2px 10px rgba(0,0,0,0.05)', position:'relative', overflow:'hidden' }}>
                    <div style={{ position:'absolute', top:0, left:0, width:4, height:'100%', background:c.border }} />
                    <div style={{ fontSize:10, letterSpacing:1.5, textTransform:'uppercase', color:'#6B6B6B', marginBottom:8, fontWeight:600 }}>{c.label}</div>
                    <div style={{ fontFamily:'Cormorant Garamond, serif', fontSize:32, fontWeight:600, color:'#1B3B5F', lineHeight:1 }}>{loading ? '…' : c.value}</div>
                  </div>
                ))}
              </div>

              {/* Chart */}
              <div style={{ background:'#fff', borderRadius:12, padding:28, boxShadow:'0 2px 12px rgba(0,0,0,0.06)', marginBottom:24 }}>
                <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:20 }}>
                  <div style={{ fontFamily:'Cormorant Garamond, serif', fontSize:20, fontWeight:600, color:'#1B3B5F' }}>Registration Trend</div>
                  <div style={{ display:'flex', gap:16 }}>
                    {[['total','#1B3B5F'],['confirmed','#2D5F4F'],['rejected','#E85D4A']].map(([k,c]) => (
                      <div key={k} style={{ display:'flex', alignItems:'center', gap:6, fontSize:12, color:'#6B6B6B' }}>
                        <svg width="24" height="10"><line x1="0" y1="5" x2="24" y2="5" stroke={c} strokeWidth="2.5" strokeLinecap="round"/><circle cx="12" cy="5" r="3" fill={c}/></svg>
                        <span style={{ textTransform:'capitalize' }}>{k}</span>
                      </div>
                    ))}
                  </div>
                </div>
                {loading ? (
                  <div style={{ height:200, display:'flex', alignItems:'center', justifyContent:'center', color:'#C4BEB8' }}>Loading…</div>
                ) : (
                  <LineChart data={chartData} keys={['total','confirmed','rejected']} colors={{ total:'#1B3B5F', confirmed:'#2D5F4F', rejected:'#E85D4A' }} />
                )}
              </div>

              {/* Filters */}
              <div style={{ display:'flex', gap:12, alignItems:'center', flexWrap:'wrap', marginBottom:20 }}>
                <span style={{ fontSize:11, letterSpacing:1.5, textTransform:'uppercase', color:'#6B6B6B', fontWeight:600 }}>Filter by:</span>
                <select style={S.select} value={filterStatus} onChange={e => { setFStatus(e.target.value); setPage(1); }}>
                  <option value="all">All Statuses</option>
                  <option value="APPROVED">Confirmed</option>
                  <option value="PENDING">Pending</option>
                  <option value="REJECTED">Rejected</option>
                  <option value="DRAFT">Draft</option>
                </select>
                <select style={S.select} value={filterCity} onChange={e => { setFCity(e.target.value); setPage(1); }}>
                  <option value="all">All Cities</option>
                  {cities.map(c => <option key={c} value={c}>{c.charAt(0).toUpperCase()+c.slice(1)}</option>)}
                </select>
                <select style={S.select} value={filterYear} onChange={e => { setFYear(e.target.value); setPage(1); }}>
                  <option value="all">All Years</option>
                  {[2024,2025,2026].map(y => <option key={y} value={y}>{y}</option>)}
                </select>
                <button style={{ ...S.btn('outline'), marginLeft:'auto' }} onClick={() => { setFStatus('all'); setFCity('all'); setFYear('all'); setSearch(''); setPage(1); }}>Reset</button>
              </div>

              {/* Table */}
              <div style={{ background:'#fff', borderRadius:12, boxShadow:'0 2px 12px rgba(0,0,0,0.06)', overflow:'hidden' }}>
                <div style={{ padding:'18px 24px', display:'flex', alignItems:'center', justifyContent:'space-between', borderBottom:'1px solid #E8E4DF', flexWrap:'wrap', gap:12 }}>
                  <div style={{ fontFamily:'Cormorant Garamond, serif', fontSize:18, fontWeight:600, color:'#1B3B5F' }}>Registration Records</div>
                  <div style={{ display:'flex', gap:10, alignItems:'center' }}>
                    <div style={{ display:'flex', alignItems:'center', gap:8, background:'#F8F6F3', borderRadius:8, padding:'8px 14px', border:'1.5px solid transparent' }}>
                      <span style={{ color:'#6B6B6B', fontSize:14 }}>🔍</span>
                      <input placeholder="Search students…" value={search}
                        onChange={e => { setSearch(e.target.value); setPage(1); }}
                        style={{ border:'none', background:'transparent', fontFamily:'Work Sans, sans-serif', fontSize:13, color:'#2C2C2C', outline:'none', width:180 }} />
                    </div>
                    <button style={S.btn('gold')} onClick={exportXLSX}>⬇ Export XLSX</button>
                  </div>
                </div>

                <table style={{ width:'100%', borderCollapse:'collapse' }}>
                  <thead>
                    <tr>
                      {[['ref','Reg ID'],['name','Full Name'],['date','Date & Time']].map(([col, label]) => (
                        <th key={col} style={S.th} onClick={() => sortBy(col)}>
                          {label} {sort.col === col ? (sort.dir === 'asc' ? '↑' : '↓') : '↕'}
                        </th>
                      ))}
                      <th style={S.th}>City / Region</th>
                      <th style={S.th}>Contact</th>
                      <th style={S.th}>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      <tr><td colSpan={6} style={{ padding:'60px 0', textAlign:'center', color:'#C4BEB8', fontSize:14 }}>Loading…</td></tr>
                    ) : pageData.length === 0 ? (
                      <tr><td colSpan={6} style={{ padding:'60px 0', textAlign:'center' }}>
                        <div style={{ fontSize:44, opacity:0.3, marginBottom:14 }}>🔍</div>
                        <div style={{ fontFamily:'Cormorant Garamond, serif', fontSize:20, color:'#6B6B6B', marginBottom:6 }}>No records found</div>
                        <div style={{ fontSize:13, color:'#C4BEB8' }}>Try adjusting your filters or search query.</div>
                      </td></tr>
                    ) : pageData.map((s, i) => (
                      <tr key={s.id || i} onMouseEnter={e => e.currentTarget.style.background='rgba(27,59,95,0.02)'} onMouseLeave={e => e.currentTarget.style.background=''}>
                        <td style={{ ...S.td, fontFamily:'monospace', fontSize:12.5, color:'#6B6B6B' }}>{s.registrationId || '—'}</td>
                        <td style={{ ...S.td, fontWeight:600, color:'#1B3B5F' }}>{s.fullName || '—'}</td>
                        <td style={S.td}>{fmtDateTime(s.createdAt)}</td>
                        <td style={S.td}>{s.nativeRegion || '—'}</td>
                        <td style={S.td}>{s.mobile || '—'}</td>
                        <td style={S.td}><StatusPill status={s.status} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {/* Pagination */}
                <div style={{ padding:'13px 22px', display:'flex', alignItems:'center', justifyContent:'space-between', borderTop:'1px solid #E8E4DF', background:'#F8F6F3' }}>
                  <span style={{ fontSize:12, color:'#6B6B6B' }}>
                    Showing {totalFiltered === 0 ? 0 : (page-1)*PAGE_SIZE+1}–{Math.min(page*PAGE_SIZE, totalFiltered)} of {totalFiltered} records
                  </span>
                  <div style={{ display:'flex', gap:4 }}>
                    {[{label:'‹', p:page-1}, ...Array.from({length:Math.min(totalPages,5)},(_,i)=>({label:String(i+1),p:i+1})), {label:'›',p:page+1}].map(({label,p:tp},i) => (
                      <button key={i} onClick={() => { if (tp >= 1 && tp <= totalPages) setPage(tp); }}
                        disabled={(label === '‹' && page === 1) || (label === '›' && page === totalPages)}
                        style={{ width:32, height:32, borderRadius:6, border:`1.5px solid ${tp === page ? '#1B3B5F' : '#E8E4DF'}`, background: tp === page ? '#1B3B5F' : '#fff', color: tp === page ? '#fff' : '#6B6B6B', fontSize:12, fontWeight:600, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', fontFamily:'Work Sans, sans-serif', opacity: ((label==='‹'&&page===1)||(label==='›'&&page===totalPages)) ? 0.4 : 1 }}>
                        {label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default ReportsScreen;